import Api from '../../services/Api';
import Helpers from '../../js/helpers';
import GisFrontCache from '../../services/GisFrontCache';
import { GIS_WARMUP_DEBOUNCE_MS, GIS_FRONT_CACHE_TTL_MS } from '../../js/constants';
import { getWideningStartDates } from '../../js/utils/gisDateRange';

// Guard anti-repetición del warmup: variables de módulo (no reactivas), mismo patrón
// que email/accessToken/refreshToken en Api.js. No protegen a Geoserver (eso ya lo hace
// la caché de 6h/24h del backend) sino a nuestro propio backend de peticiones
// redundantes si el usuario selecciona la misma plantación varias veces seguidas
// desde el Buscador.
let lastGisWarmupPlantationId = null;
let lastGisWarmupTimestamp = 0;

// Pide a GIS los índices de la línea temporal de un rango, sin procesarlos.
async function requestTimelineIndexes(
  { rootGetters, state },
  {
    location, initDate, endDate, companyId,
  },
) {
  if (rootGetters['Settings/isGisV2']) {
    // GAP DE PARIDAD conocido y aceptado (igual que fetchDatesLayer): gis/v2/index_stats
    // no filtra las fechas excluidas por el usuario (LocationGISDate). No inventar aquí
    // ese filtrado en cliente: revisar cuando el equipo GIS lo desarrolle.
    await Api.renewAccessToken();
    const params = new URLSearchParams({
      ws: 'timacagro',
      client: 'timacagro',
      layer: location,
      init_date: initDate,
      end_date: endDate,
    });
    state.indexAvailables.forEach((index) => params.append('index', index));
    const response = await fetch(`${Api.getBaseUrl()}/gis/v2/index_stats?${params}`, {
      headers: {
        Authorization: Api.getHeaderAuth(),
        company: companyId,
      },
    });
    return response.json().then((res) => res.data);
  }
  const xhr = await Api.fetchTimelineIndexes(
    location,
    initDate,
    endDate,
    companyId,
  );
  return xhr.json().then((res) => res.data);
}

export default {
  namespaced: true,
  state: {
    osm: {
      route: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      type: 'OSM',
      properties: {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    },
    googleHybrid: {
      route:
        'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}?apikey=AIzaSyAlUKxY4pMzMwUK7OQ9N737Zj0TbLcW6TA',
      type: 'Google',
      properties: {
        maxZoom: 19,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      },
    },
    currentLayersMap: [],
    selectedLayerMap: null,
    showSecondMap: false,
    dateList: [],
    currentOverlayLayer: '',
    currentPoint: {
      latitude: '',
      longitude: '',
    },
    currentIndexes: {},
    indexAvailables: ['NDVI', 'NDVI_CONT', 'SAVI', 'ARVI', 'VARI', 'LAI', 'NDRE', 'RECI', 'NDMI', 'GNDVI', 'GLI', 'TCI', 'MSAVI'],
    currentGraphicLayer: {},
    currentBoundsWMSLayer: {},
    currentInitIntervalDates: {},
    currentEndIntervalDates: {},
    currentGisWMS: null,
    currentGisComparatorLeft: null,
    currentGisComparatorRight: null,
    currentGisScanningLeft: null,
    currentGisScanningRight: null,
    datesList: [],
    cloudsValues: [],
    featureInfo: {},
    currentGeojsonFeature: {},
    histogram: null,
    timelineIndexes: null,
    histogramValues: [],
    parcelCentroide: {},
    sectorCentroide: {},
    currentIndex: '',
  },
  getters: {
    osm: (state) => state.osm,
    googleHybrid: (state) => state.googleHybrid,
    currentLayersMap: (state) => state.currentLayersMap,
    selectedLayerMap: (state) => state.selectedLayerMap,
    showSecondMap: (state) => state.showSecondMap,
    // Base de la capa WMS (tiles/leyenda) para Leaflet: no puede ser una action async
    // porque los componentes la necesitan de forma síncrona al montar la capa.
    // eslint-disable-next-line no-unused-vars
    wmsBaseUrl: (state, getters, rootState, rootGetters) => (
      rootGetters['Settings/isGisV2']
        ? Api.getGeoServerProjectWorkSpace()
        : Api.getGeoServerProjectWorkSpaceLegacy()
    ),
  },
  actions: {
    resetCurrentPoint(context) {
      context.commit('setCurrentPoint', {
        latitude: '',
        longitude: '',
      });
    },
    setCurrentPoint(context, newCurrentPoint) {
      context.commit('setCurrentPoint', newCurrentPoint);
    },
    setParcelCentroide({ commit }, value) {
      commit('setParcelCentroide', value);
    },
    setSectorCentroide({ commit }, value) {
      commit('setSectorCentroide', value);
    },
    setOsm(context, value) {
      context.commit('setOsm', value);
    },
    setGoogleHybrid(context, value) {
      context.commit('setGoogleHybrid', value);
    },
    setCurrentLayersMap(context, value) {
      context.commit('setCurrentLayersMap', value);
    },
    setSelectedLayerMap(context, value) {
      context.commit('setSelectedLayerMap', value);
    },
    setShowSecondMap(context) {
      context.commit('setShowSecondMap');
    },
    setDateList(context, value) {
      context.commit('setDateList', value);
    },
    addCurrentLayersMap({ commit, state }, value) {
      for (const layerMap of state.currentLayersMap) {
        if (layerMap === value) return;
      }
      commit('addCurrentLayersMap', value);
    },
    removeCurrentLayersMap(context, value) {
      context.commit('removeCurrentLayersMap', value);
    },
    setCurrentOverlayLayer(context, value) {
      context.commit('setCurrentOverlayLayer', value);
    },
    setCurrentGisWMS(context, value) {
      context.commit('setCurrentGisWMS', value);
    },
    setCurrentGisComparatorLeft(context, value) {
      context.commit('setCurrentGisComparatorLeft', value);
    },
    setCurrentGisComparatorRight(context, value) {
      context.commit('setCurrentGisComparatorRight', value);
    },
    setCurrentGisScanningLeft(context, value) {
      context.commit('setCurrentGisScanningLeft', value);
    },
    setCurrentGisScanningRight(context, value) {
      context.commit('setCurrentGisScanningRight', value);
    },
    async fetchGraphicLayer({ commit, state }, { currentOverlayLayer, key }) {
      try {
        const requestOptions = {
          method: 'GET',
          redirect: 'follow',
        };
        const url = `${Api.getGeoServer()}/get_raster_stats/?layer=${currentOverlayLayer}&dates=${state.currentInitIntervalDates
          },${state.currentEndIntervalDates}&index=${state.currentIndexes[
            key
          ].toLowerCase()}`;
        await fetch(url, requestOptions)
          .then((response) => response.text())
          .then((result) => {
            commit('setCurrentGraphicLayer', JSON.parse(result));
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    async boundsWMSLayer({ commit, rootGetters }, { currentOverlayLayer, companyId }) {
      try {
        const [ws, layer] = currentOverlayLayer.split(':');
        let result;
        if (rootGetters['Settings/isGisV2']) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 120000);
          try {
            await Api.renewAccessToken();
            const url = `${Api.getBaseUrl()}/gis/v2/layer_bounds?ws=${ws}&client=${ws}&layer=${layer}`;
            const text = await fetch(url, {
              method: 'GET',
              redirect: 'follow',
              signal: controller.signal,
              headers: {
                Authorization: Api.getHeaderAuth(),
                company: companyId,
              },
            }).then((response) => response.text());
            result = JSON.parse(text);
          } finally {
            clearTimeout(timeoutId);
          }
        } else {
          // getbbox va directo a GIS (no pasa por nuestro backend, ver Api.getGeoServer()),
          // así que se cachea aquí en el propio frontend (localStorage, TTL de
          // GIS_FRONT_CACHE_TTL_MS) para no repetir esta llamada, la más lenta observada.
          // getOrFetch además deduplica: si el warmup disparado al seleccionar la
          // plantación (warmupGisFrontCache) ya tiene esta misma petición en vuelo, nos
          // enganchamos a ella en vez de lanzar otra.
          result = await GisFrontCache.getOrFetch(currentOverlayLayer, GIS_FRONT_CACHE_TTL_MS, () => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000);
            const url = `${Api.getGeoServer()}/getbbox/?ws=${ws}&layer=${layer}&ds=undefined`;
            return fetch(url, { method: 'GET', redirect: 'follow', signal: controller.signal })
              .then((response) => response.text())
              .then((text) => JSON.parse(text))
              .finally(() => clearTimeout(timeoutId));
          });
        }
        commit('setCurrentBoundsWMSLayer', result);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    // Fire-and-forget: no se espera esta llamada, no debe retrasar la navegación ni
    // mostrarse al usuario si falla. Solo tiene efecto en legacy: en v2, warmupGisCache
    // ya precalienta bounds en el backend (gis/v2/layer_bounds, cacheado ahí), así que
    // repetir el warmup aquí sería una llamada real duplicada e innecesaria. Precalienta
    // getbbox (vía boundsWMSLayer, ya cache-aware) para el sector de la plantación
    // recién seleccionada en el Buscador.
    warmupGisFrontCache({ dispatch, rootGetters }, { sectorId }) {
      if (!sectorId || rootGetters['Settings/isGisV2']) return;
      dispatch('boundsWMSLayer', { currentOverlayLayer: `timacagro:${sectorId}` }).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('No se pudo precalentar getbbox para el sector', sectorId, error);
      });
    },
    async fetchDatesLayer({ commit, rootGetters }, { layer, companyId }) {
      try {
        if (rootGetters['Settings/isGisV2']) {
          // GAP DE PARIDAD conocido y aceptado (ver timac_django GISIndexDatesView):
          // a diferencia de service_gis/get_raster_dates (legacy), gis/v2/index_dates
          // NO filtra las fechas excluidas por el usuario (LocationGISDate) — GIS no
          // soporta ese parámetro todavía (confirmado contra su swagger de preproducción
          // y producción, agosto 2026). No inventar aquí un filtrado en cliente: revisar
          // cuando el equipo GIS lo publique.
          await Api.renewAccessToken();
          const url = `${Api.getBaseUrl()}/gis/v2/index_dates?ws=timacagro&client=timacagro&layer=${layer}`;
          await fetch(url, {
            method: 'GET',
            redirect: 'follow',
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
          })
            .then((response) => response.json())
            .then((result) => {
              // Mismo contrato que expone el legacy /service_gis/get_raster_dates/ (que a su
              // vez ya proxya a gis/v2/index_dates y reenvuelve la respuesta así): el resto
              // del código (GISLayerOptionsComparator, CloudCoverage, plantillas) espera un
              // objeto con la clave "avaiable_dates" (con la errata original), no un array suelto.
              commit('setDatesLayer', { avaiable_dates: result.available_dates });
            })
            .catch((error) => Promise.reject(error));
          return Promise.resolve();
        }

        // No se manda "server": el backend ya resuelve el host de GIS con su propia config
        // (GIS_SETTINGS.url); confirmado en timac_django que ese parámetro se ignoraba.
        // El endpoint sigue siendo service_gis/get_raster_dates (Django): esa vista filtra
        // las fechas excluidas por el usuario (LocationGISDate), lógica que no existe en
        // gis/v2/index_dates — no se debe llamar a ese v2 directo desde el frontend.
        const xhr = await Api.getElement(
          `/service_gis/get_raster_dates/`,
          {
            layer: layer,
            ws: 'timacagro',
          },
          companyId,
          15000,
        );
        commit('setDatesLayer', JSON.parse(xhr.response));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    // Devuelve las coordenadas del centroide ([lng, lat]), o un objeto {error} si
    // el criterio de búsqueda no encuentra resultados — mismo contrato que ya
    // esperaba el caller (registerParcelStep2.vue::fieldDataChanged) con la
    // llamada legacy directa a GIS.
    async fetchSigpacCentroid({ rootGetters }, { queryParamsData, companyId }) {
      const queryParams = Api.getQueryParams(queryParamsData);

      if (rootGetters['Settings/isGisV2']) {
        await Api.renewAccessToken();
        const response = await fetch(
          `${Api.getBaseUrl()}/gis/v2/search_sigpac_centroid?${queryParams}`,
          {
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
          },
        );
        if (!response.ok) {
          return { error: true };
        }
        const result = await response.json();
        return result.centroide;
      }

      const xhr = await Api.getFetch(`${Api.getGeoServer()}/getcentroidsigpac/?${queryParams}`);
      return xhr.json();
    },
    // Devuelve el Response de la imagen satelital (image/png) para un layer/fecha/índice
    // dados. Centraliza en un único sitio el branching legacy/v2 que antes estaba
    // duplicado en 4 componentes (GISWMSMap, GISWMSTimeDimensionMap, y los reports de
    // analysis/searcher), todos construyendo la misma URL a mano.
    async fetchSatelliteImage({ rootGetters }, {
      companyId, layer, date, index, env, resize, quality, dimension,
    }) {
      if (rootGetters['Settings/isGisV2']) {
        await Api.renewAccessToken();
        const params = new URLSearchParams({ ws: 'timacagro', layer, date, index });
        // No recortar "env" por ";" (min/max/mean/stddev): a diferencia del shim legacy
        // (ver _raw_query_param en timac_django), gis/v2/satellite_image_index no está
        // mitigado para el truncado de query strings con ";" de Python 3.8 — pasar tal cual.
        if (env) params.set('env', env);
        if (resize) params.set('resize', resize);
        if (quality) params.set('quality', quality);
        if (dimension) params.set('dimension', dimension);
        return fetch(`${Api.getBaseUrl()}/gis/v2/satellite_image_index?${params}`, {
          headers: {
            Authorization: Api.getHeaderAuth(),
            company: companyId,
          },
        });
      }

      let queryString = `?ws=${Api.getGeoServerWorkspace()}&layer=${layer}&date=${date}&index=${index}&env=${env}`;
      if (resize) queryString += `&resize=${resize}`;
      if (quality) queryString += `&quality=${quality}`;
      if (dimension) queryString += `&dimension=${dimension}`;
      return Api.getGeoServerCallServices(companyId, Api.getGeoServerPrintMap(), queryString);
    },
    async fetchClouds({ rootGetters }, { location, date, companyId }) {
      return rootGetters['Settings/isGisV2']
        ? Api.getGeoServerClouds(location, date, companyId)
        : Api.getGeoServerCloudsLegacy(location, date);
    },
    async fetchSigpacSurface({ rootGetters }, { polygon, companyId }) {
      return rootGetters['Settings/isGisV2']
        ? Api.geoserverSigpacSurfaceData(polygon, companyId)
        : Api.geoserverSigpacSurfaceDataLegacy(polygon);
    },
    async fetchMunicipalitiesByProvince({ rootGetters }, { province, companyId }) {
      return rootGetters['Settings/isGisV2']
        ? Api.getMunicipalitiesByProvince(province, companyId)
        : Api.getMunicipalitiesByProvinceLegacy(province);
    },
    async fetchFeatureInfo({ commit, rootGetters }, { queryParams, companyId }) {
      try {
        let url;
        let requestOptions;
        if (rootGetters['Settings/isGisV2']) {
          await Api.renewAccessToken();
          url = `${Api.getGeoServerV2ProxyBase()}/${queryParams}`;
          requestOptions = {
            method: 'GET',
            redirect: 'follow',
            headers: {
              Authorization: Api.getHeaderAuth(),
              company: companyId,
            },
          };
        } else {
          url = `${Api.getGeoServerPort()}/${queryParams}`;
          requestOptions = { method: 'GET', redirect: 'follow' };
        }
        await fetch(url, requestOptions)
          .then((response) => response.text())
          .then((result) => {
            commit('setFeatureInfo', JSON.parse(result));
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    async fetchCurrentGeojsonFeature({ commit }, url) {
      try {
        const requestOptions = {
          method: 'GET',
          redirect: 'follow',
        };

        await fetch(url, requestOptions)
          .then((response) => response.json())
          .then((result) => {
            commit('setCurrentGeojsonFeature', result);
          })
          .catch((error) => Promise.reject(error));
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error); // Esta llamada no tiene los mensajes de error normalizados
      }
    },
    setCurrentIndexes({ commit }, { key, newCurrentIndex }) {
      commit('setCurrentIndexes', { key, newCurrentIndex });
    },
    setCurrentInitIntervalDates(
      { commit },
      { key, newCurrentInitIntervalDate },
    ) {
      commit('setCurrentInitIntervalDates', {
        key,
        newCurrentInitIntervalDate,
      });
    },
    setCurrentEndIntervalDates({ commit }, { key, newCurrentEndIntervalDate }) {
      commit('setCurrentEndIntervalDates', { key, newCurrentEndIntervalDate });
    },
    setHistogram({ commit }, value) {
      commit('setHistogram', value);
    },
    async fetchHistogram({ commit, rootGetters, state }, { location, date, company }) {
      try {
        let result;
        if (rootGetters['Settings/isGisV2']) {
          // GAP DE PARIDAD conocido y aceptado (igual que fetchDatesLayer): gis/v2/index_stats
          // no filtra las fechas excluidas por el usuario (LocationGISDate). No inventar aquí
          // ese filtrado en cliente: revisar cuando el equipo GIS lo desarrolle.
          await Api.renewAccessToken();
          const params = new URLSearchParams({
            ws: 'timacagro',
            client: 'timacagro',
            layer: location,
            init_date: date,
            histogram: 'true',
          });
          state.indexAvailables.forEach((index) => params.append('index', index));
          const response = await fetch(`${Api.getBaseUrl()}/gis/v2/index_stats?${params}`, {
            headers: {
              Authorization: Api.getHeaderAuth(),
              company,
            },
          });
          result = await response.json().then((res) => res.data);
        } else {
          const xhr = await Api.getGeoServerTemperatureHistogram(
            location,
            date,
            company,
          );
          result = await xhr.json().then((res) => res.data);
        }

        commit('setHistogramValues', result.length > 0 ? result[0].values : []);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchTimelineIndexes(
      { commit, rootGetters, state },
      {
        location, initDate: requestedInitDate, endDate, companyId, widenRangeIfEmpty = false,
      },
    ) {
      try {
        // Con widenRangeIfEmpty, si el rango pedido no tiene imágenes se amplía a 3 meses
        // y luego a 1 año, igual que el mapa (GISWMSTimeDimensionMap), EDSER-2138.
        const startDates = widenRangeIfEmpty
          ? getWideningStartDates(requestedInitDate, endDate)
          : [requestedInitDate];
        let result;
        let initDate;
        for (const startDate of startDates) {
          initDate = startDate;
          // eslint-disable-next-line no-await-in-loop
          result = await requestTimelineIndexes({ rootGetters, state }, {
            location, initDate, endDate, companyId,
          });
          if (Array.isArray(result) && result.length > 0) break;
        }

        // Si result esta vacio forzamos para que vaya por el catch error
        if (!Array.isArray(result) || result.length === 0) {
          throw new Error('Error en llamada GIS: No data');
        }

        const first = result?.[0];
        const last = result?.[result.length - 1];

        if (last?.date !== endDate) {
          result.push(
            {
              date: endDate,
              location: last?.location ?? '',
              values: [
                {
                  index: 'arvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'gli',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'gndvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'lai',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'msavi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndmi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndre',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'reci',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'savi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'vari',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndvi_cont',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
              ],
            },
          );
        }

        if (first?.date !== initDate) {
          result.push(
            {
              date: initDate,
              location: first?.location ?? '',
              values: [
                {
                  index: 'arvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'gli',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'gndvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'lai',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'msavi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndmi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndre',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndvi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'reci',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'savi',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'vari',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
                {
                  index: 'ndvi_cont',
                  count: null,
                  min: null,
                  max: null,
                  mean: null,
                  stddev: null,
                },
              ],
            },
          );
        }

        commit('setTimelineIndexes', result);
        // Rango usado finalmente, para que la vista avise si se ha ampliado
        return { initDate, endDate, widened: initDate !== requestedInitDate };
      } catch (error) {
        return Promise.reject(Helpers.getError(error));
      }
    },
    setTimelineIndexes({ commit }, value) {
      commit('setTimelineIndexes', value);
    },
    setHistogramValues({ commit }, value) {
      commit('setHistogramValues', value);
    },
    setCloudsValues({ commit }, value) {
      commit('setCloudsValues', value);
    },
    // Fire-and-forget: no se hace await de esta action desde los componentes. Un
    // fallo aquí no debe afectar a la navegación ni mostrarse al usuario.
    warmupGisCache(_context, { companyId, plantationId }) {
      if (!plantationId) return;

      const now = Date.now();
      if (
        plantationId === lastGisWarmupPlantationId
        && now - lastGisWarmupTimestamp < GIS_WARMUP_DEBOUNCE_MS
      ) {
        return;
      }
      lastGisWarmupPlantationId = plantationId;
      lastGisWarmupTimestamp = now;

      Api.warmupGisCache(companyId, plantationId).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('No se pudo precalentar la caché GIS de la plantación', plantationId, error);
      });
    },
  },
  mutations: {
    setCurrentGeoJSONLayer(state, newCurrentGeoJSONLayer) {
      state.currentGeoJSONLayer = newCurrentGeoJSONLayer;
    },
    setCloudsValues(state, value) {
      state.cloudsValues = value;
    },
    setParcelCentroide(state, value) {
      state.parcelCentroide = value;
    },
    setSectorCentroide(state, value) {
      state.sectorCentroide = value;
    },
    setCurrentPoint(state, newCurrentPoint) {
      state.currentPoint = newCurrentPoint;
    },
    setOsm(state, value) {
      state.osm = value;
    },
    setGoogleHybrid(state, value) {
      state.googleHybrid = value;
    },
    setCurrentLayersMap(state, value) {
      state.currentLayersMap = value;
    },
    setSelectedLayerMap(state, value) {
      state.selectedLayerMap = value;
    },
    setShowSecondMap(state) {
      state.showSecondMap = !state.showSecondMap;
    },
    setDateList(state, value) {
      state.dateList = value;
    },
    addCurrentLayersMap(state, value) {
      state.currentLayersMap.push(value);
    },
    removeCurrentLayersMap(state, value) {
      const newCurrentLayersMap = [];
      for (const currentLayersMap of state.currentLayersMap) {
        if (currentLayersMap !== value) {
          newCurrentLayersMap.push(currentLayersMap);
        }
      }
      state.currentLayersMap = newCurrentLayersMap;
    },
    setCurrentOverlayLayer(state, value) {
      state.currentOverlayLayer = value;
    },
    setCurrentGraphicLayer(state, newCurrentGraphicLayer) {
      state.currentGraphicLayer = newCurrentGraphicLayer;
    },
    setCurrentBoundsWMSLayer(state, newCurrentBoundsWMSLayer) {
      state.currentBoundsWMSLayer = newCurrentBoundsWMSLayer;
    },
    setDatesLayer(state, newDatesLayer) {
      state.datesList = newDatesLayer;
    },
    setFeatureInfo(state, newFeatureInfor) {
      state.featureInfo = newFeatureInfor;
    },
    setCurrentGeojsonFeature(state, newCurrentGeojsonFeature) {
      state.currentGeojsonFeature = newCurrentGeojsonFeature;
    },
    setCurrentIndexes(state, { key, newCurrentIndex }) {
      state.currentIndexes[key] = newCurrentIndex;
      state.currentIndex = newCurrentIndex;
    },
    setCurrentIntervalDates(state, { key, newIntervalDate }) {
      state.currentIntervalDates[key] = newIntervalDate;
    },
    setCurrentInitIntervalDates(state, { key, newCurrentInitIntervalDate }) {
      state.currentInitIntervalDates[key] = newCurrentInitIntervalDate;
    },
    setCurrentEndIntervalDates(state, { key, newCurrentEndIntervalDate }) {
      state.currentEndIntervalDates[key] = newCurrentEndIntervalDate;
    },
    setCurrentGisWMS(state, value) {
      state.currentGisWMS = value;
    },
    setCurrentGisComparatorLeft(state, value) {
      state.currentGisComparatorLeft = value;
    },
    setCurrentGisComparatorRight(state, value) {
      state.currentGisComparatorRight = value;
    },
    setCurrentGisScanningLeft(state, value) {
      state.currentGisScanningLeft = value;
    },
    setCurrentGisScanningRight(state, value) {
      state.currentGisScanningRight = value;
    },
    setHistogram(state, value) {
      state.histogram = value;
    },
    setTimelineIndexes(state, value) {
      state.timelineIndexes = value;
    },
    setHistogramValues(state, value) {
      state.histogramValues = value;
    },
  },
};
